import { TIERS } from '../content'
import { GroupId } from '../config/systemConfig'
import { warn } from '../utils/logger'

/**
 * Placement Engine — Date-based phase resolution and starting tier logic.
 *
 * NOTE: The original diagnostic placement *test* concept was removed (B/M/E
 * phase is now derived from the calendar date). The functions in this file
 * (resolvePhaseByDate, getAutoStartingTier, getAutoPhaseLabel) are actively
 * used by the adaptive engine's cold-start logic.
 *
 * RC pass (mei 2026): the legacy `getPlacementKey` helper and the unused
 * `PlacementResult` import have been removed — `savePlacementResult` and the
 * `placementResults` AppState field were already stripped in
 * PR_SELECTEDGROUP_CLEANUP, leaving no callers.
 */

// ─── §4.4 Date-Based Placement Strategy ───────────────────────────────────────
// Merged from dateBasedPlacement.ts (stabilization §4.4/22)
// Provides automatic starting tier based on school year phase.

export type SchoolYearPhase = 'B' | 'M' | 'E'

/**
 * Resolve the school year phase based on the current date.
 *
 * Logic:
 * - Aug 1 (Month 7) – Oct 31 (Month 9) = 'B' (Begin)
 * - Nov 1 (Month 10) – Mar 31 (Month 2) = 'M' (Midden)
 * - Apr 1 (Month 3) – Jul 31 (Month 6) = 'E' (Eind)
 */
export function resolvePhaseByDate(date: Date = new Date()): SchoolYearPhase {
  const month = date.getMonth() // 0 = Jan, 11 = Dec

  // Aug (7) to Oct (9) -> B
  if (month >= 7 && month <= 9) return 'B'

  // Nov (10) to Dec (11) OR Jan (0) to Mar (2) -> M
  if (month >= 10 || month <= 2) return 'M'

  // Apr (3) to Jul (6) -> E
  return 'E'
}

/**
 * Get the automatic starting tier for a group based on the current date.
 * Returns the tier of the first level (non-plus) for the resolved phase.
 */
export function getAutoStartingTier(
  group: GroupId,
  date: Date = new Date(),
): number {
  const phase = resolvePhaseByDate(date)

  // Find the tier matching group + phase + !isPlus
  const tierConfig = TIERS.find(
    (t) => t.group === group && t.period === phase && !t.isPlus,
  )
  if (tierConfig) return tierConfig.tier

  // Fallback: return the first tier of the group
  const firstTier = TIERS.find((t) => t.group === group)
  if (firstTier) return firstTier.tier

  // Defensive last-resort fallback — only reachable if TIERS data is
  // corrupt (no entry exists for `group` at all). The previous ladder
  // (`group===6?1:group===7?7:13`) duplicated tier-bandgrenzen as magic
  // numbers; we instead surface the corruption via a warning so it can
  // be diagnosed rather than silently returning an incorrect tier.
  // — R6-5 fix, mei 2026.
  warn(
    `[placementEngine] No tier found for group ${group} — TIERS data corrupt? Falling back to tier 1.`,
  )
  return 1
}

/**
 * Get a human-readable label for the current auto-detected phase.
 */
export function getAutoPhaseLabel(date: Date = new Date()): string {
  const phase = resolvePhaseByDate(date)
  switch (phase) {
    case 'B':
      return 'Begin schooljaar'
    case 'M':
      return 'Midden schooljaar'
    case 'E':
      return 'Eind schooljaar'
  }
}
